import { useCallback, useState } from 'react';
import type {
  DraftVariantRead,
  GenerationRequestDetailRead,
  GenerationRequestRead,
  RejectionReason,
} from '../../types/api';
import type { SendBackPath } from './sendBack';
import { useRedesignSlides } from './useRedesignSlides';
import type { RejectionFlow } from './useRejectRequest';
import { useReviseVariant } from './useReviseVariant';

export interface SendBackFlow {
  /** The dialog is on screen. */
  isOpen: boolean;
  isSubmitting: boolean;
  /** Whatever the last attempt said, whichever path it took. */
  error: string | null;
  /** Regeneration attempts spent on this lineage, out of three. */
  regenerationsUsed: number;
  /** Revisions spent on this lineage, out of five. Redesigns count here too. */
  revisionsUsed: number;
  isRegenerationCapped: boolean;
  isRevisionCapped: boolean;
  open: () => void;
  close: () => void;
  /** The chip moved; the message from the path before it no longer applies. */
  clearError: () => void;
  submit: (path: SendBackPath, reason: RejectionReason, feedback: string) => void;
}

/**
 * One reviewer, one complaint, three very different bills — and this is where
 * the complaint is turned into the right one.
 *
 * The screens used to carry two separate controls (Reject, Revise) that asked
 * the same question and charged different prices for the answer, which left the
 * choice of *mechanism* to someone who only knows what is wrong with the draft.
 * So there is now one entry point per card and per screen, the dialog asks what
 * kind of problem it is, and this hook routes it:
 *
 * - a wording complaint (`copy_tone`, `copy_facts`, `copy_length`) is a
 *   **revision** of this caption — one model call, everything else kept;
 * - a complaint about the pictures (`visual`) is a **redesign** — one design
 *   call over the same caption and the same backdrops;
 * - a wrong angle, or anything else, is a real **regeneration** — the only path
 *   that spends one of the lineage's three attempts, and the only one that
 *   opens a new request.
 *
 * The two budgets stay separate and are both read off the request row, so they
 * survive a reload: `attempt` for regenerations, `revisions` for the other two,
 * which share theirs. Spending one never touches the other.
 *
 * Regeneration is the **screen's**, not the dialog's, and is passed in: it opens
 * a new request and navigates away, and the cap it runs into has to be answered
 * once per screen (the SRS's two ways forward) rather than once per card.
 */
export function useSendBack(
  businessId: string,
  request: GenerationRequestDetailRead,
  /** The caption a revision would rewrite: this card's, or the chosen one. */
  variant: DraftVariantRead | null,
  /** The request as the server handed it back — `revising`, or `designing`. */
  onApplied: (next: GenerationRequestRead) => void,
  /** The screen's own rejection flow, which owns the cap and the navigation. */
  rejection: RejectionFlow,
): SendBackFlow {
  const [isOpen, setIsOpen] = useState(false);

  // A revision and a redesign come back as the same row this page is already
  // holding, so the dialog's work is done the moment the response lands; the
  // page follows the request onto the stream from there.
  const handleApplied = useCallback(
    (next: GenerationRequestRead) => {
      setIsOpen(false);
      onApplied(next);
    },
    [onApplied],
  );

  const revision = useReviseVariant(businessId, variant, request.revisions, handleApplied);
  const redesign = useRedesignSlides(businessId, request.id, request.revisions, handleApplied);

  const clearError = useCallback(() => {
    revision.reset();
    redesign.reset();
    rejection.reset();
  }, [redesign, rejection, revision]);

  const open = useCallback(() => {
    clearError();
    setIsOpen(true);
  }, [clearError]);

  const close = useCallback(() => {
    clearError();
    setIsOpen(false);
  }, [clearError]);

  const submit = useCallback(
    (path: SendBackPath, reason: RejectionReason, feedback: string) => {
      switch (path) {
        case 'revise':
          revision.submit(feedback, reason);
          return;
        case 'redesign':
          redesign.submit(feedback);
          return;
        case 'regenerate':
          // Not closed here: a rejection navigates to the new request, and a
          // refusal (the cap, or a spent quota) has to land somewhere the
          // reviewer is still looking.
          rejection.submit(feedback, reason);
      }
    },
    [redesign, rejection, revision],
  );

  return {
    isOpen,
    isSubmitting: revision.isSubmitting || redesign.isSubmitting || rejection.isSubmitting,
    // At most one of these is set: every path clears all three before it sends.
    error: revision.error ?? redesign.error ?? rejection.error,
    regenerationsUsed: rejection.used,
    revisionsUsed: revision.used,
    isRegenerationCapped: rejection.isCapped,
    isRevisionCapped: revision.isCapped || redesign.isCapped,
    open,
    close,
    clearError,
    submit,
  };
}
